import React from 'react';
import {
  Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { BlocGraphique } from '../../types/chat';

// Palette : vert Kanea en premier, puis couleurs distinctes pour les comparaisons.
const COULEURS = ['#22c55e', '#3b82f6', '#f59e0b', '#a855f7', '#ef4444', '#64748b'];

interface LigneRecharts {
  x: string;
  [serie: string]: string | number | null;
}

const estDate = (x: string) => /^\d{4}-\d{2}-\d{2}$/.test(x);

const formatAxe = (x: string) => (estDate(x) ? `${x.slice(8, 10)}/${x.slice(5, 7)}/${x.slice(2, 4)}` : x);

const formatValeur = (v: number | string | null | undefined, unite: string) =>
  typeof v === 'number' ? `${v.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}${unite ? ` ${unite}` : ''}` : '—';

// Les séries partagent l'axe des abscisses : on fusionne leurs points par date/libellé.
const fusionne = (bloc: BlocGraphique): LigneRecharts[] => {
  const lignes = new Map<string, LigneRecharts>();
  bloc.series.forEach((serie) => {
    serie.points.forEach(([x, v]) => {
      const ligne = lignes.get(x) ?? { x };
      ligne[serie.name] = v;
      lignes.set(x, ligne);
    });
  });
  return Array.from(lignes.values()).sort((a, b) => (a.x < b.x ? -1 : a.x > b.x ? 1 : 0));
};

export const ChatChart: React.FC<{ bloc: BlocGraphique }> = ({ bloc }) => {
  const donnees = fusionne(bloc);
  const plusieurs = bloc.series.length > 1;
  const commun = (
    <>
      <CartesianGrid strokeDasharray="3 3" stroke="rgba(156,163,175,0.15)" />
      <XAxis dataKey="x" tickFormatter={formatAxe} tick={{ fontSize: 10, fill: 'var(--text-muted, #888)' }} minTickGap={24} />
      <YAxis tick={{ fontSize: 10, fill: 'var(--text-muted, #888)' }} width={56}
             tickFormatter={(v: number) => v.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} domain={['auto', 'auto']} />
      <Tooltip
        formatter={(v: number | string) => formatValeur(typeof v === 'number' ? v : Number(v), bloc.unit)}
        labelFormatter={(x: string) => formatAxe(x)}
        contentStyle={{ fontSize: 12, borderRadius: 8, background: 'var(--bg-box, #fff)', border: '1px solid var(--border-color, #e8e8e8)' }}
      />
      {plusieurs && <Legend wrapperStyle={{ fontSize: 11 }} />}
    </>
  );

  return (
    <figure className="chat-chart" style={{ margin: '10px 0', padding: '10px 12px', border: '0.5px solid var(--border-color, #e8e8e8)', borderRadius: 10, background: 'var(--bg-box, #fff)' }}>
      <figcaption style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-heading, #111)', marginBottom: 6 }}>{bloc.title}</figcaption>
      <div style={{ width: '100%', height: 240 }}>
        <ResponsiveContainer>
          {bloc.kind === 'bar' ? (
            <BarChart data={donnees}>
              {commun}
              {bloc.series.map((s, i) => <Bar key={s.name} dataKey={s.name} fill={COULEURS[i % COULEURS.length]} radius={[3, 3, 0, 0]} />)}
            </BarChart>
          ) : (
            <LineChart data={donnees}>
              {commun}
              {bloc.series.map((s, i) => (
                <Line key={s.name} type="monotone" dataKey={s.name} stroke={COULEURS[i % COULEURS.length]} strokeWidth={2} dot={false} connectNulls />
              ))}
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
      <div style={{ fontSize: 10, color: 'var(--text-muted, #888)', marginTop: 4 }}>
        Source : {bloc.source}{bloc.date_donnees ? ` — séance du ${formatAxe(bloc.date_donnees)}` : ''}
      </div>
    </figure>
  );
};
